import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Warning, Key, ArrowsClockwise, Trash, CurrencyInr, EnvelopeSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function IntegrationsSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [provider, setProvider] = useState("gstverify");
  const [apiKey, setApiKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/integrations");
      setState(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Paste the API key first"); return; }
    setSaving(true);
    try {
      await api.put("/settings/integrations/gstin", {
        provider: provider.trim().toLowerCase(),
        api_key: apiKey.trim(),
      });
      toast.success("GSTIN key saved — live lookups now active");
      setApiKey(""); setShowForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const testFetch = async () => {
    const t = toast.loading("Testing GSTIN fetch on '27ATCPC8433A1Z7'…");
    try {
      const { data } = await api.post("/gstin/verify?refresh=1", { gstin: "27ATCPC8433A1Z7" });
      toast.dismiss(t);
      if (data.source === "external_api" && data.trade_name) {
        toast.success(`✓ Live fetch works — resolved "${data.trade_name}" (${data.state})`);
      } else if (data.source === "cache") {
        toast.success(`✓ Cache hit — "${data.trade_name}" (${data.state})`);
      } else {
        toast.error(`Fetch returned '${data.source}' — provider not configured or credits exhausted.`);
      }
    } catch (err) { toast.dismiss(t); toast.error(formatApiError(err)); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove the stored GSTIN key? Live lookups will stop.")) return;
    try {
      await api.delete("/settings/integrations/gstin");
      toast.success("GSTIN key removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const g = state?.gstin;
  const r = state?.razorpay;

  // Razorpay form state
  const [rpForm, setRpForm] = useState(false);
  const [rpKeyId, setRpKeyId] = useState("");
  const [rpSecret, setRpSecret] = useState("");
  const [rpWebhook, setRpWebhook] = useState("");
  const [rpSaving, setRpSaving] = useState(false);

  const saveRazorpay = async () => {
    if (!rpKeyId.trim() || !rpSecret.trim()) { toast.error("Paste both Key ID and Key Secret"); return; }
    setRpSaving(true);
    try {
      await api.put("/settings/integrations/razorpay", {
        key_id: rpKeyId.trim(),
        key_secret: rpSecret.trim(),
        webhook_secret: rpWebhook.trim(),
      });
      toast.success("Razorpay keys saved — Pay Now is now active");
      setRpKeyId(""); setRpSecret(""); setRpWebhook(""); setRpForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRpSaving(false); }
  };

  const clearRazorpay = async () => {
    if (!window.confirm("Remove the stored Razorpay keys? Pay Now will stop working.")) return;
    try {
      await api.delete("/settings/integrations/razorpay");
      toast.success("Razorpay keys removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Daily Close Report Recipients ────────────────────────────────
  // Admin manages the list here; every "Close Day" fans the report out to
  // every configured address. Falls back to the company/user email if empty.
  const [dcNewEmail, setDcNewEmail] = useState("");
  const [dcSaving, setDcSaving] = useState(false);
  const dc = state?.daily_close;

  const persistRecipients = async (list) => {
    setDcSaving(true);
    try {
      await api.put("/settings/daily-close-recipients", { recipients: list });
      await load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally { setDcSaving(false); }
  };

  const addRecipient = async () => {
    const e = dcNewEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email address"); return; }
    const list = [...(dc?.recipients || [])];
    if (list.includes(e)) { toast.error("Already in the list"); return; }
    list.push(e);
    if (await persistRecipients(list)) {
      toast.success(`${e} will now receive the daily closing report`);
      setDcNewEmail("");
    }
  };

  const removeRecipient = async (email) => {
    if (!window.confirm(`Stop sending the daily closing report to ${email}?`)) return;
    const list = (dc?.recipients || []).filter((x) => x !== email);
    if (await persistRecipients(list)) toast.success(`${email} removed`);
  };

  // ── WhatsApp UI removed ─────────────────────────────────────────
  // Server-side WhatsApp provider was retired. Outreach now happens
  // via `wa.me/` deep-links on Payment Pending, Sales Invoice, and Cheque
  // rows — no per-app credentials required.

  return (
    <div className="page-shell space-y-6">
      <div>
        <div className="eyebrow">Settings</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage API keys used by the app for external services. Changes take effect immediately — no redeploy required.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
              <Key size={18} className="text-primary" weight="bold" />
            </div>
            <div>
              <CardTitle className="text-base">GSTIN Lookup</CardTitle>
              <div className="text-xs text-muted-foreground mt-0.5">
                Auto-fills company name, address and state when a customer or supplier's GSTIN is entered.
              </div>
            </div>
          </div>
          <div>
            {loading ? (
              <span className="pill pill-info">Loading…</span>
            ) : g?.configured ? (
              <span className="pill pill-success">
                <CheckCircle size={11} weight="fill" className="mr-1" /> Configured
              </span>
            ) : (
              <span className="pill pill-danger">
                <Warning size={11} weight="fill" className="mr-1" /> Not configured
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {g && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
              <div>
                <div className="eyebrow mb-0.5">Provider</div>
                <div className="font-mono">{g.provider || "—"}</div>
              </div>
              <div>
                <div className="eyebrow mb-0.5">API Key (masked)</div>
                <div className="font-mono">{g.api_key_masked || "—"}</div>
              </div>
              <div>
                <div className="eyebrow mb-0.5">Source</div>
                <div className="font-mono">
                  {g.source === "database" ? "🗃️  In-app (recommended)"
                    : g.source === "env" ? "⚙️  Backend .env file"
                    : "—"}
                </div>
              </div>
              {g.updated_at && (
                <div className="md:col-span-3 text-[11.5px] text-muted-foreground">
                  Last updated: {String(g.updated_at).slice(0, 16).replace("T", " ")} by {g.updated_by || "—"}
                </div>
              )}
            </div>
          )}

          {isAdmin ? (
            !showForm ? (
              <div className="flex items-center gap-2 flex-wrap pt-2">
                <Button size="sm" onClick={() => setShowForm(true)} data-testid="gstin-cfg-edit">
                  <Key size={14} className="mr-1.5" />
                  {g?.configured ? "Update key" : "Add API key"}
                </Button>
                {g?.configured && (
                  <>
                    <Button size="sm" variant="outline" onClick={testFetch} data-testid="gstin-cfg-test">
                      <ArrowsClockwise size={14} className="mr-1.5" /> Test live fetch
                    </Button>
                    {g?.source === "database" && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={clearKey} data-testid="gstin-cfg-clear">
                        <Trash size={14} className="mr-1.5" /> Remove
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3 pt-2 border-t border-border/70">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Provider</Label>
                    <select
                      value={provider}
                      onChange={(e) => setProvider(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                      data-testid="gstin-cfg-provider"
                    >
                      <option value="gstverify">gstverify — gstverify.co.in</option>
                      <option value="appyflow">appyflow — appyflow.in</option>
                      <option value="surepass">surepass — surepass.io</option>
                    </select>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="eyebrow">API Key</Label>
                    <Input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Paste the API key here (e.g. gstv_…)"
                      className="h-9 font-mono"
                      data-testid="gstin-cfg-key"
                    />
                    <div className="text-[11px] text-muted-foreground">
                      Key is stored encrypted-at-rest in MongoDB and only ever shown masked afterward.
                      Never checked into git.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={save} disabled={saving} data-testid="gstin-cfg-save">
                    {saving ? "Saving…" : "Save & activate"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setApiKey(""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="text-[12.5px] text-muted-foreground italic pt-2">
              Only administrators can edit integration keys.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-[11.5px] text-muted-foreground max-w-2xl">
        <b>Note:</b> On production (vetricherp.com), a key saved here is written to your MongoDB and picked up on the very next GSTIN lookup — no restart or redeploy required. If the key is unset both here <i>and</i> in the backend .env file, GSTIN lookups fall back to state-only (derived from the first 2 digits).
      </div>

      {/* ─── Razorpay ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
              <CurrencyInr size={18} className="text-primary" weight="bold" />
            </div>
            <div>
              <CardTitle className="text-base">Razorpay Payment Links</CardTitle>
              <div className="text-xs text-muted-foreground mt-0.5">
                Lets customers pay any unpaid invoice via UPI, card or netbanking. Adds a "Pay Now" button on the public invoice page.
              </div>
            </div>
          </div>
          <div>
            {loading ? (
              <span className="pill pill-info">Loading…</span>
            ) : r?.configured ? (
              <span className={`pill ${r.mode === "live" ? "pill-success" : "pill-info"}`}>
                <CheckCircle size={11} weight="fill" className="mr-1" />
                {r.mode === "live" ? "LIVE" : "TEST"} mode
              </span>
            ) : (
              <span className="pill pill-danger">
                <Warning size={11} weight="fill" className="mr-1" /> Not configured
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {r && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
              <div>
                <div className="eyebrow mb-0.5">Key ID</div>
                <div className="font-mono">{r.key_id_masked || "—"}</div>
              </div>
              <div>
                <div className="eyebrow mb-0.5">Key Secret</div>
                <div className="font-mono">{r.key_secret_masked || "—"}</div>
              </div>
              <div>
                <div className="eyebrow mb-0.5">Webhook</div>
                <div className="font-mono">
                  {r.webhook_configured ? "✓ Configured" : "— not set"}
                </div>
              </div>
              {r.updated_at && (
                <div className="md:col-span-3 text-[11.5px] text-muted-foreground">
                  Last updated: {String(r.updated_at).slice(0, 16).replace("T", " ")} by {r.updated_by || "—"}
                </div>
              )}
            </div>
          )}

          {isAdmin ? (
            !rpForm ? (
              <div className="flex items-center gap-2 flex-wrap pt-2">
                <Button size="sm" onClick={() => setRpForm(true)} data-testid="rzp-cfg-edit">
                  <Key size={14} className="mr-1.5" />
                  {r?.configured ? "Update keys" : "Add Razorpay keys"}
                </Button>
                {r?.configured && r?.source === "database" && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={clearRazorpay} data-testid="rzp-cfg-clear">
                    <Trash size={14} className="mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3 pt-2 border-t border-border/70">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Key ID</Label>
                    <Input
                      value={rpKeyId} onChange={(e) => setRpKeyId(e.target.value)}
                      placeholder="rzp_test_... or rzp_live_..."
                      className="h-9 font-mono" data-testid="rzp-cfg-keyid"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="eyebrow">Key Secret</Label>
                    <Input
                      type="password"
                      value={rpSecret} onChange={(e) => setRpSecret(e.target.value)}
                      placeholder="Paste the secret shown once at key creation"
                      className="h-9 font-mono" data-testid="rzp-cfg-secret"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="eyebrow">Webhook Secret <span className="text-muted-foreground">(optional now — required after you set the webhook URL)</span></Label>
                    <Input
                      type="password"
                      value={rpWebhook} onChange={(e) => setRpWebhook(e.target.value)}
                      placeholder="Paste after you set the webhook in Razorpay Dashboard"
                      className="h-9 font-mono" data-testid="rzp-cfg-webhook"
                    />
                    <div className="text-[11px] text-muted-foreground">
                      After saving, go to Razorpay Dashboard → Settings → Webhooks → Add. Use URL <span className="font-mono">{typeof window !== "undefined" ? `${window.location.origin.replace(/^https?:\/\//, "https://")}/api/razorpay/webhook` : "https://<your-domain>/api/razorpay/webhook"}</span> and subscribe to <span className="font-mono">payment_link.paid</span> and <span className="font-mono">payment.captured</span>. Then paste the shown secret above and Save again.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveRazorpay} disabled={rpSaving} data-testid="rzp-cfg-save">
                    {rpSaving ? "Saving…" : "Save & activate"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setRpForm(false); setRpKeyId(""); setRpSecret(""); setRpWebhook(""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="text-[12.5px] text-muted-foreground italic pt-2">
              Only administrators can edit integration keys.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Daily Close Report Recipients ───────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
              <EnvelopeSimple size={18} className="text-primary" weight="bold" />
            </div>
            <div>
              <CardTitle className="text-base">Daily Close Report Recipients</CardTitle>
              <div className="text-xs text-muted-foreground mt-0.5">
                Every time someone clicks <b>Close Day</b> on the Daily Cash Close page, the closing report is emailed to <i>all</i> of these addresses. Add owners, accountants, auditors — up to 20.
              </div>
            </div>
          </div>
          <div>
            {loading ? (
              <span className="pill pill-info">Loading…</span>
            ) : dc?.configured ? (
              <span className="pill pill-success">
                <CheckCircle size={11} weight="fill" className="mr-1" />
                {dc.recipients_count} recipient{dc.recipients_count > 1 ? "s" : ""}
              </span>
            ) : (
              <span className="pill pill-danger">
                <Warning size={11} weight="fill" className="mr-1" /> No recipients — using fallback
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {!dc?.configured && (
            <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-[12.5px]" style={{ color: "hsl(var(--warning))" }}>
              No recipients configured. The report is currently being sent to your login email as a fallback. Add at least one address below so the daily close report reaches the right people.
            </div>
          )}

          {(dc?.recipients || []).length > 0 && (
            <div className="space-y-2">
              <Label className="eyebrow">Active recipients</Label>
              <div className="flex flex-wrap gap-2">
                {dc.recipients.map((e) => (
                  <div key={e}
                    className="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-3 pr-1 py-1 text-[12.5px]"
                    data-testid={`dc-recipient-${e}`}
                  >
                    <span className="font-mono">{e}</span>
                    {isAdmin && (
                      <button
                        onClick={() => removeRecipient(e)}
                        disabled={dcSaving}
                        className="h-5 w-5 flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"
                        aria-label={`Remove ${e}`}
                        data-testid={`dc-remove-${e}`}
                      >
                        <X size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAdmin ? (
            <div className="space-y-2 pt-2 border-t border-border/70">
              <Label className="eyebrow">Add new recipient</Label>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  type="email"
                  value={dcNewEmail}
                  onChange={(e) => setDcNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                  placeholder="owner@company.com"
                  className="h-9 font-mono flex-1 min-w-[220px] max-w-md"
                  data-testid="dc-new-email"
                />
                <Button size="sm" onClick={addRecipient} disabled={dcSaving} data-testid="dc-add-btn">
                  <Plus size={14} className="mr-1.5" weight="bold" /> Add
                </Button>
              </div>
              {dc?.updated_at && (
                <div className="text-[11px] text-muted-foreground">
                  Last updated: {String(dc.updated_at).slice(0, 16).replace("T", " ")} by {dc.updated_by || "—"}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[12.5px] text-muted-foreground italic pt-2">
              Only administrators can edit the recipients list.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
